import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { Hash, Plus, X, Sparkles, AlertTriangle } from "lucide-react";

/**
 * Phase 10 — Inline lot picker for a single material line.
 *
 * Loads active lots for the given itemId and lets the user split the
 * line quantity across one or more lots (FIFO suggestions by default).
 *
 * Props:
 *  - itemId            : inventory item id (raw material)
 *  - quantity          : line qty to allocate (number)
 *  - allocations       : current allocations [{lot_id, lot_number, quantity}]
 *  - onChange(list)    : called with updated allocation list
 *  - tracked           : boolean — toggle traceability on/off for this line
 *  - onTrackedChange(b): called when switch toggles
 */
export function LotPickerInline({ itemId, quantity, allocations, onChange, tracked, onTrackedChange }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itemId) { setLots([]); return; }
    setLoading(true);
    api.get(`/tanks/lots/by-item/${itemId}`)
      .then(({ data }) => setLots(data.lots || []))
      .catch(() => setLots([]))
      .finally(() => setLoading(false));
  }, [itemId]);

  const totalAllocated = useMemo(
    () => (allocations || []).reduce((s, a) => s + (Number(a.quantity) || 0), 0),
    [allocations]
  );
  const remainingNeeded = Math.max(0, Number(quantity || 0) - totalAllocated);
  const overAllocated = totalAllocated > Number(quantity || 0) + 1e-6;

  const updateAlloc = (i, k, v) => {
    const next = [...(allocations || [])];
    next[i] = { ...next[i], [k]: v };
    if (k === "lot_id") {
      const lt = lots.find((l) => l.id === v);
      next[i].lot_number = lt?.lot_number || null;
    }
    onChange(next);
  };
  const addAlloc = () => {
    const used = new Set((allocations || []).map((a) => a.lot_id));
    const next = lots.find((l) => !used.has(l.id));
    onChange([...(allocations || []), {
      lot_id: next?.id || "",
      lot_number: next?.lot_number || null,
      quantity: remainingNeeded || 0,
    }]);
  };
  const removeAlloc = (i) => onChange((allocations || []).filter((_, idx) => idx !== i));

  const autoFifo = () => {
    let need = Number(quantity || 0);
    const out = [];
    for (const lt of lots) {
      if (need <= 1e-9) break;
      const avail = Number(lt.quantity || 0);
      if (avail <= 0) continue;
      const take = Math.min(avail, need);
      out.push({ lot_id: lt.id, lot_number: lt.lot_number, quantity: Number(take.toFixed(6)) });
      need -= take;
    }
    onChange(out);
  };

  if (!itemId) {
    return (
      <div className="text-[11px] text-muted-foreground italic">
        Pick a material first to enable lot tracking
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-dashed border-border p-2 bg-muted/20">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[11px] flex items-center gap-1">
          <Hash className="w-3 h-3" /> Lot tracking
        </Label>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">{tracked ? "ON" : "OFF"}</span>
          <Switch checked={!!tracked} onCheckedChange={onTrackedChange} data-testid="lot-picker-toggle" />
        </div>
      </div>

      {tracked && (
        <>
          {loading ? (
            <div className="text-[11px] text-muted-foreground">Loading lots…</div>
          ) : lots.length === 0 ? (
            <div className="rounded border border-amber-300/60 bg-amber-50/40 dark:bg-amber-900/10 p-2 text-[11px] text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              No active lots for this item. Record a Goods Intake with a lot # first.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">
                  {lots.length} active lot{lots.length === 1 ? "" : "s"} · total avail{" "}
                  <span className="font-mono">{lots.reduce((s, l) => s + Number(l.quantity || 0), 0)}</span>
                </span>
                <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={autoFifo} data-testid="lot-picker-fifo">
                  <Sparkles className="w-3 h-3 mr-1" />Auto FIFO
                </Button>
              </div>

              {(allocations || []).length === 0 && (
                <div className="text-[11px] text-muted-foreground italic">
                  Click "Auto FIFO" or add lots manually.
                </div>
              )}

              <div className="space-y-1.5">
                {(allocations || []).map((a, i) => {
                  const lt = lots.find((l) => l.id === a.lot_id);
                  return (
                    <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`lot-alloc-${i}`}>
                      <div className="col-span-7">
                        <select
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
                          value={a.lot_id}
                          onChange={(e) => updateAlloc(i, "lot_id", e.target.value)}
                          data-testid={`lot-alloc-${i}-select`}
                        >
                          <option value="">— pick a lot —</option>
                          {lots.map((l) => (
                            <option key={l.id} value={l.id} disabled={Boolean((allocations || []).some((x, idx) => x.lot_id === l.id && idx !== i))}>
                              {l.lot_number} · avail {l.quantity}{l.expiry_date ? ` · exp ${new Date(l.expiry_date).toLocaleDateString()}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-4">
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          className="h-8 text-xs"
                          value={a.quantity}
                          onChange={(e) => updateAlloc(i, "quantity", e.target.value)}
                          placeholder="Qty"
                          data-testid={`lot-alloc-${i}-qty`}
                        />
                        {lt && Number(a.quantity || 0) > Number(lt.quantity || 0) && (
                          <div className="text-[10px] text-destructive">Exceeds lot ({lt.quantity})</div>
                        )}
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeAlloc(i)} data-testid={`lot-alloc-${i}-remove`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between text-[11px] pt-1">
                <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={addAlloc}>
                  <Plus className="w-3 h-3 mr-1" />Add lot
                </Button>
                <div className={overAllocated ? "text-destructive" : "text-muted-foreground"}>
                  Allocated <span className="font-mono">{totalAllocated.toFixed(2)}</span> /{" "}
                  <span className="font-mono">{Number(quantity || 0).toFixed(2)}</span>
                  {!overAllocated && remainingNeeded > 0 && (
                    <span className="ml-1">(need <span className="font-mono">{remainingNeeded.toFixed(2)}</span> more)</span>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
